// src/pages/CadastroUnidadeConsumidora.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Formulario from '../../components/ui/Formulario';

// Contexto e hooks
import { UserContext } from '../../UserContext';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';

// Estilos
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import styles from './CadastroUnidadeConsumidora.module.css';

function DetalharUnidadeConsumidora() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { get, post, put } = useHttpClient();
    const { exibirErro } = useNotificacoes();
    const { user } = useContext(UserContext);

    const [formData, setFormData] = useState({
        idUsina: '',
        idUsuario: '',
        codCliente: '',
        nomeUnidade: '',
        distribuidora: '',
        localizacaoUnidade: ''
    });

    const [dataLoaded, setDataLoaded] = useState(false);
    const ranOnce = useRef(false);

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const idEmpresa = userData.empresa?.id || 0;

    const [usuarios, setUsuarios] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [distribuidoras, setDistribuidoras] = useState([]);

    // helpers ---------------------------------------------------------
    const fetchUsinas = async () => {
        const filtrosUsinas = new URLSearchParams({ idEmpresa }).toString();
        const r = await get(`/hello-sol/usinas/buscar?${filtrosUsinas}`);
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setUsinas(lista);
        return lista;
    };

    const fetchDistribuidoras = async () => {
        const r = await get(`/hello-sol/unidades-consumidoras/distribuidoras`);
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setDistribuidoras(lista);
        return lista;
    };

    const fetchClientesDaUsina = async (idUsina) => {
        if (!idUsina) { setUsuarios([]); return []; }
        const r = await get(
            `/hello-sol/usuarios/buscarUsuarioUsina?` +
            new URLSearchParams({ idUsina, idTipoUsuario: 3 }).toString()
        );
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setUsuarios(lista);
        return lista;
    };

    // Descobre a usina a partir do cliente, percorrendo as usinas da empresa
    const detectUsinaByCliente = async (clienteId, listaUsinas) => {
        for (const u of listaUsinas) {
            try {
                const r = await get(
                    `/hello-sol/usuarios/buscarUsuarioUsina?` +
                    new URLSearchParams({ idUsina: u.id, idTipoUsuario: 3 }).toString()
                );
                const arr = Array.isArray(r) ? r : (r?.content ?? []);
                if (arr.some(c => String(c.id) === String(clienteId))) {
                    return String(u.id);
                }
            } catch {
                // ignora e segue para a próxima usina
            }
        }
        return '';
    };
    // ----------------------------------------------------------------

    // fluxo: carrega usinas -> (edição) registro -> detecta usina se necessário -> clientes da usina -> form -> libera
    useEffect(() => {
        if (!idEmpresa) return;
        if (ranOnce.current) return; // evita 2x no StrictMode (dev)
        ranOnce.current = true;

        (async () => {
            try {
                const todasUsinas = await fetchUsinas();

                // 🔹 carrega distribuidoras
                await fetchDistribuidoras();

                if (id) {
                    const d = await get(`/hello-sol/unidades-consumidoras/${id}`);

                    const clienteId = String(d?.cliente?.id ?? d?.idUsuario ?? d?.idCliente ?? '');
                    let usinaId = String(d?.usina?.id ?? d?.idUsina ?? d?.usinaId ?? '');

                    // se o DTO não informar a usina, detecta pela relação cliente->usina
                    if (!usinaId && clienteId) {
                        usinaId = await detectUsinaByCliente(clienteId, todasUsinas);
                    }

                    if (usinaId) {
                        await fetchClientesDaUsina(usinaId);
                    }

                    // garante que o cliente apareça na combo mesmo se não vier na lista
                    if (clienteId) {
                        setUsuarios(curr => {
                            const has = curr.some(u => String(u.id) === clienteId);
                            if (has) return curr;
                            const nomeCliente = d?.cliente?.nome ?? 'Cliente selecionado';
                            return [{ id: Number(clienteId), nome: nomeCliente }, ...curr];
                        });
                    }

                    setFormData({
                        idUsina: usinaId,
                        idUsuario: clienteId,
                        codCliente: d?.codCliente ?? '',
                        nomeUnidade: d?.nomeUnidade ?? '',
                        localizacaoUnidade: d?.localizacaoUnidade ?? '',
                        distribuidora: d?.distribuidora ?? ''
                    });
                }

                setDataLoaded(true);
            } catch (e) {
                console.error('Falha ao carregar unidade consumidora:', e);
                exibirErro(
                    e?.response?.data?.message ||
                    e?.message ||
                    'Erro ao carregar unidade consumidora.'
                );
                setDataLoaded(true);
            }
        })();
    }, [idEmpresa, id]); // mantém deps

    // quando escolher uma usina no cadastro novo, carregar os clientes dessa usina
    useEffect(() => {
        if (!formData.idUsina || id) return; // em edição já carregamos e o select fica desabilitado
        (async () => {
            try {
                await fetchClientesDaUsina(formData.idUsina);
            } catch {
                exibirErro('Erro ao buscar clientes da usina.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.idUsina, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const novo = { ...prev, [name]: value };

            // se trocar a usina em CADASTRO NOVO, limpa dependentes
            if (name === 'idUsina' && !id) {
                novo.idUsuario = '';
                novo.codCliente = '';
                novo.nomeUnidade = '';
                novo.localizacaoUnidade = '';
            }

            return novo;
        });
    };

    const salvarUnidadeConsumidora = async () => {
        try {
            const payload = {
                // campos que o back realmente usa
                idCliente: Number(formData.idUsuario),           // << AQUI
                codCliente: formData.codCliente,
                nomeUnidade: formData.nomeUnidade,
                localizacaoUnidade: formData.localizacaoUnidade,
                // se quiser, pode enviar idUsina também (se o back ignorar, ok)
                // idUsina: Number(formData.idUsina || 0),
            };

            if (id) await put(`/hello-sol/unidades-consumidoras/${id}`, payload);
            else     await post(`/hello-sol/unidades-consumidoras`, payload);

            navigate('/home/listar-unidade-consumidora');
        } catch (err) {
            exibirErro(err?.message || 'Erro ao salvar o unidade consumidora.');
        }
    };

    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarUnidadeConsumidora);

    return (
        <div className={styles_global.container}>
            <h2>{'Detalhar Unidade Consumidora'}</h2>

            <Formulario onSubmit={safeSubmit}>
                {/* Usina / Cliente */}
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsina">Usina*</label>
                        <select
                            id="idUsina"
                            className={styles.campoUsina}
                            name="idUsina"
                            value={formData.idUsina}
                            onChange={handleChange}
                            required
                            disabled
                        >
                            <option value="">Selecionar Usina</option>
                            {usinas.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="idUsuario">Cliente*</label>
                        <select
                            id="idUsuario"
                            name="idUsuario"
                            className={styles.campoCliente}
                            value={formData.idUsuario}
                            onChange={handleChange}
                            required
                            disabled
                        >
                            <option value="">Selecionar Cliente</option>
                            {usuarios.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Códigos / nomes / localização */}
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="distribuidora">Distribuidora</label>
                        <select
                            id="distribuidora"
                            name="distribuidora"
                            value={formData.distribuidora}
                            onChange={handleChange}
                            disabled
                        >
                            <option value="">Todas as Distribuidoras</option>
                            {distribuidoras.map(d => (
                                <option key={d.name} value={d.name}>
                                    {d.display} ({d.uf})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="codCliente">Cód. Unidade Consumidora</label>
                        <input
                            type="text"
                            className={styles.campoCodigo}
                            name="codCliente"
                            value={formData.codCliente}
                            onChange={handleChange}
                            id="codCliente"
                            maxLength={15}
                            disabled
                        />
                    </div>
                </div>
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="nomeUnidade">Nome Unidade</label>
                        <input
                            type="text"
                            className={styles.campoUnidade}
                            name="nomeUnidade"
                            value={formData.nomeUnidade}
                            onChange={handleChange}
                            id="nomeUnidade"
                            maxLength={150}
                            disabled
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="localizacaoUnidade">Localização da Unidade</label>
                        <input
                            id="localizacaoUnidade"
                            name="localizacaoUnidade"
                            type="text"
                            value={formData.localizacaoUnidade}
                            onChange={handleChange}
                            className={styles.campoLocalizacao}
                            placeholder="Bloco A"
                            disabled
                        />
                    </div>
                </div>

                {/* 🔥 TUSD */}
                <div className={styles_content.formField}>
                    <label>Aplicar Desconto TUSD</label>
                    <input
                        value={formData.stAplicarDescontoTusd === 1 ? 'Sim' : 'Não'}
                        disabled
                    />
                </div>

                <div className={styles_content.buttonGroup}>
                    <button
                        type="button"
                        onClick={() => navigate('/home/listar-unidade-consumidora')}
                    >
                        Voltar
                    </button>
                </div>
            </Formulario>
        </div>
    );
}

export default DetalharUnidadeConsumidora;
